import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape SUP-03 publishes, imported rather than restated.
import type { TimelineEntry } from "@tdelivr/api-client/generated/support/support-timeline.js";

import { exportSheet, type ExportFormat } from "../export.ts";
import {
  eventCell,
  fetchedNote,
  PER_FETCH,
  sourcesPresent,
  stalenessOf,
  stateOf,
  statesPresent,
  timelineDetail,
  timelinePage,
  whenCell,
  type TimelineOrder,
} from "../timeline.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { GridPager, GridToolbar, NoMatch } from "./DataGrid.tsx";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Everything that happened on one case, oldest first, across every system.
 *
 * Built to `20 WP19 Support Console.dc.html`, page `timeline`, on that file's one `gridUI()`.
 *
 * **This is the screen where the platform and the design agree most closely.** The design's three
 * columns are `When`, `Source` and `Event`; SUP-03 publishes `occurredAt`, `source` and `summary`,
 * with `category` for the badge and an optional `reference` for the identifier the design appends.
 * ADR 0078 published the public read, and found that the authorization was already right and had
 * been since the service was written: `TimelineService.list` decides **per entry** from that row's
 * own `participantSubjects` and `authorizedAgentSubjects`, so a customer sees the entries they are
 * on and an agent holding `support-timeline:agent-read` sees the case. Nothing was loosened.
 *
 * **It is also the only board in this repository whose staleness has a published source.**
 * CLAUDE.md section 1 requires the stale state be built, and everywhere else in this console and
 * the other three it has had to be recorded as unanswerable: no listing publishes a page-level
 * instant. SUP-03 publishes `stale: boolean` **and** a checkpoint per source carrying
 * `lastSequence` and `projectedAt`, so the board can say which system is behind and by how long
 * rather than hedging about the whole page.
 *
 * **The primary action has nowhere to go, and this one is worth stating plainly.** The design's
 * `Add note or reply` form collects a type, a macro, a message and a status. **No reply operation
 * exists anywhere on the platform** — not on SUP-03, whose two writes are `rebuild` and `project`
 * and are both a projection's own business; not on SUP-01, whose `sensitive-notes` and
 * `transitions` are `/internal/v1`; not on any of the other 140 services. So the single thing this
 * console exists to let an agent do, it cannot do. The button is drawn and refused, and that
 * absence is the most valuable thing on this screen to record.
 *
 * **No tiles, where the design draws four.** `Reply due · 12 min · SLA` has no source at all — see
 * `cases.ts` for the measurement. `Events · 11` and `Customer messages · 3` are counts over a
 * cursor-paged listing that publishes no total (R-04), so both would be counts of what this page
 * happened to fetch wearing the label of a case. `Systems involved · 4` is the one that *is*
 * derivable, and a single tile where the design draws four rows would read as three missing tiles
 * rather than as a measurement — so the sources are named in the sentence under the grid instead,
 * where the rest of what this board actually fetched is already stated.
 *
 * **A timeline is a view of a case, so something has to say which case.** The design's own heading
 * is "Everything that happened on case cs_4471". Opened with no case chosen, this draws the
 * designed empty state saying so rather than an error or a blank — the queue is where a case is
 * chosen, and choosing one there brings its identifier here.
 */

const timelineApi = services.support__support_timeline;

export interface CaseTimelineProps {
  readonly transport: Transport;
  readonly tenantId: string;
  /** The case to show, chosen on the queue. Absent until one has been. */
  readonly caseId?: string | undefined;
  /** Innermost last: tenant, console, group, page. */
  readonly crumbs?: readonly string[];
  /** Send the agent to the queue, which is where a case is chosen. */
  readonly onOpenQueue?: () => void;
}

interface Loaded {
  readonly items: readonly TimelineEntry[];
  /** Absent on the last page rather than empty. */
  readonly cursor: string | undefined;
  readonly checkpoints: readonly { source: string; projectedAt: string }[];
  readonly stale: boolean;
}

export function CaseTimeline({
  transport,
  tenantId,
  caseId,
  crumbs,
  onOpenQueue,
}: CaseTimelineProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [fetching, setFetching] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedEntryId, setSelectedEntryId] = useState<string | undefined>(undefined);
  const [state, setState] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<TimelineOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);

  const load = useCallback(async () => {
    if (caseId === undefined) {
      setLoaded(undefined);
      setFailure(undefined);
      return;
    }
    const client = timelineApi.createSupportTimelineClient(transport);
    try {
      const answered = await client.getSupportCaseTimeline(
        tenantId,
        caseId,
        undefined,
        String(PER_FETCH),
      );
      setLoaded({
        items: answered.items,
        cursor: answered.nextCursor,
        checkpoints: answered.checkpoints,
        stale: answered.stale,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["support-timeline:read"]));
      setLoaded(undefined);
    }
  }, [transport, tenantId, caseId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Later entries, from the cursor the service answered.
   *
   * Safe to follow because a projection appends and never rewrites: the cursor walks each source's
   * own `sourceSequence`, which is immutable once written, so an entry cannot move across it
   * between pages. That is the property ADR 0027 had to add a column to the refund table to get,
   * and this read model has by construction.
   */
  const loadLater = useCallback(async () => {
    const held = loaded;
    if (held?.cursor === undefined || fetching || caseId === undefined) return;
    setFetching(true);
    const client = timelineApi.createSupportTimelineClient(transport);
    try {
      const answered = await client.getSupportCaseTimeline(
        tenantId,
        caseId,
        held.cursor,
        String(PER_FETCH),
      );
      setLoaded({
        items: [...held.items, ...answered.items],
        cursor: answered.nextCursor,
        checkpoints: answered.checkpoints,
        stale: answered.stale,
      });
      setPage((current) => current + 1);
    } catch (error) {
      setFailure(stateForError(error, ["support-timeline:read"]));
    } finally {
      setFetching(false);
    }
  }, [loaded, fetching, transport, tenantId, caseId]);

  const rows = loaded?.items ?? [];
  /* Across everything fetched, so the panel survives a page turn or a narrowed search. */
  const selectedEntry = loaded?.items.find((row) => row.entryId === selectedEntryId);

  const view = useMemo(
    () => timelinePage(rows, { search, order, page, perPage }, state),
    [rows, search, order, page, perPage, state],
  );

  /*
   * Read, not computed. SUP-03 answers `stale` and a checkpoint per source, which is the one place
   * on this platform a board does not have to say it cannot tell.
   */
  const behind = useMemo(
    () => stalenessOf(loaded?.stale ?? false, loaded?.checkpoints ?? [], Date.now()),
    [loaded],
  );

  const viewState = failure ?? stateForList(loaded === undefined ? undefined : rows);

  const clearFilters = (): void => {
    setSearch("");
    setState(undefined);
    setPage(1);
  };

  const onNext = (): void => {
    if (page < view.pageCount) setPage(page + 1);
    else void loadLater();
  };

  const note = fetchedNote(
    loaded?.items.length,
    loaded?.cursor !== undefined,
    sourcesPresent(rows),
  );

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="records__head">
      <span>When</span>
      <span>Source</span>
      <span>Event</span>
      <span className="records__head--right">State</span>
    </div>
  );

  return (
    <div className="board-split">
      {/*
        The page and its detail panel in one positioned container, which is what docks the panel.

        Both docking rules are written against `.board-split`: at 1240 and above
        `.board-split:has(> .detail)` makes the panel a 420px second column, and below 1240 it is
        `position: absolute; inset: 0 0 0 auto`, positioned against `.board-split`'s own
        `position: relative`. This board had neither — the panel was rendered inside the page, and
        on seven of these twelve boards inside the closing-note callout, where it was a flex item
        beside an `info` glyph and a sentence.

        `Settlement.tsx` and `Invoices.tsx` were already this shape, which is why they are the
        shape copied here rather than a new one. Found by the census in
        `tooling/scripts/lib/detail-panels.mjs`: this panel had never been photographed, so
        nothing compared it against the design it is built from. Which docking is right at all is
        Q-297.
      */}
      <section className="page records timeline">
        <PageHeading
          crumbs={crumbs ?? []}
          title="Case timeline"
          source="SUP-03"
          description={
            caseId === undefined
              ? "Everything that happened on a case, oldest first, across every system."
              : `Everything that happened on case ${caseId}, oldest first, across every system.`
          }
          action={
            <button
              type="button"
              className="records__primary"
              /*
               * Drawn and refused, and this is the absence most worth recording on this console.
               *
               * The design's form is `Add to this case`: a type, a macro, a message and a status.
               * **No reply operation exists anywhere on the platform.** SUP-03's two writes are
               * `POST .../timeline/rebuild` and `POST .../support-timeline/events`, which are a
               * projection rebuilding itself and a projection being appended to — neither is an agent
               * writing to a customer, and both are `/internal/v1`. SUP-01 publishes
               * `sensitive-notes` and `transitions`, also `/internal/v1`, and a sensitive note is not
               * a reply either.
               *
               * So the one thing a support console exists for — answering somebody — has nowhere to
               * go. That is a service to build, not a route to publish, and it is the single most
               * valuable gap on this screen.
               */
              disabled
              title="No reply operation exists anywhere on the platform. SUP-03's two writes are a projection rebuild and a projection append, both /internal/v1; SUP-01's sensitive-notes and transitions are internal too, and neither is a reply. See R-35."
            >
              <span aria-hidden="true" className="records__primary-glyph">
                add
              </span>
              Add note or reply
            </button>
          }
        />

        {caseId === undefined ? (
          /*
           * No case chosen. The design's heading names one — `case cs_4471` — so this page is a view
           * of a case and cannot be opened without one.
           *
           * The designed empty state rather than an error, because nothing has gone wrong: the agent
           * has arrived at a page that needs a selection. It says where the selection is made and
           * offers to go there, which is the one control on this branch.
           */
          <div className="records__panel">
            <StateView
              state={{ kind: "empty", because: "no_data" }}
              purpose="Choose a case on the queue to see everything that happened on it."
            >
              <span />
            </StateView>
            {onOpenQueue === undefined ? null : (
              <p className="records__bounded">
                <button type="button" className="records__link" onClick={onOpenQueue}>
                  Open the case queue
                </button>
              </p>
            )}
          </div>
        ) : (
          <div className="records__panel">
            <GridToolbar
              idPrefix="timeline"
              search={search}
              onSearch={(next) => {
                setSearch(next);
                setPage(1);
              }}
              state={state}
              onState={(next) => {
                setState(next);
                setPage(1);
              }}
              states={statesPresent(rows)}
              order={order}
              onOrder={setOrder}
              onExport={(format: ExportFormat) => {
                exportSheet(sheetFor(view.all), format, "case-timeline");
              }}
            />

            {/*
            The stale state, drawn because the service answers it.
            Named per source, because "some of this may be behind" leaves an agent unable to tell
            whether the part they are reading is the stale part.
          */}
            {behind.length === 0 ? null : (
              <p className="records__stale" role="status">
                <span aria-hidden="true" className="records__stale-glyph">
                  history_toggle_off
                </span>
                <span>
                  SUP-03 reports this timeline is behind.{" "}
                  {behind
                    .map((entry) => `${entry.source} last projected ${entry.behindBy}`)
                    .join("; ")}
                  . Later entries from those systems may not be here yet.
                </span>
              </p>
            )}

            <StateView
              state={viewState}
              purpose="Entries appear here as each system reports what it did."
              onRetry={() => {
                void load();
              }}
              skeleton={{
                head,
                columns: 4,
                rowClassName: "records__row",
                // Both strings come from the trail the shell already draws, so the
                // sentence can never disagree with the breadcrumb above it.
                page: crumbs?.at(-1) ?? "this page",
                tenant: crumbs?.[0] ?? "this tenant",
              }}
            >
              {head}
              <div className="records__rows">
                {view.rows.map((entry) => {
                  const shown = stateOf(entry);
                  return (
                    <div
                      key={entry.entryId}
                      data-selected={entry.entryId === selectedEntryId ? "true" : undefined}
                      role="button"
                      tabIndex={0}
                      aria-pressed={entry.entryId === selectedEntryId}
                      aria-label={`Open timeline entry ${entry.entryId}`}
                      onClick={() => setSelectedEntryId(entry.entryId)}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.preventDefault();
                        setSelectedEntryId(entry.entryId);
                      }}
                      className={
                        shown.classKey === "human"
                          ? "records__row timeline__row--customer"
                          : "records__row"
                      }
                    >
                      <span
                        className="records__lead"
                        /* The date, because a case open across midnight has two days in it. */
                        title={entry.occurredAt}
                      >
                        {whenCell(entry.occurredAt)}
                      </span>
                      <span className="records__cell" title={`system ${entry.source}`}>
                        {entry.source}
                      </span>
                      <span
                        className="records__cell"
                        title={[
                          entry.summary,
                          entry.reference === undefined
                            ? undefined
                            : `reference ${entry.reference}`,
                          `source event ${entry.sourceEventId}`,
                        ]
                          .filter((part): part is string => part !== undefined)
                          .join("\n")}
                      >
                        {eventCell(entry)}
                      </span>
                      <span className="records__state">
                        <StateBadge
                          code="SUP-03"
                          status={entry.category}
                          classKey={shown.classKey}
                          label={shown.label}
                          title={`SUP-03 publishes category ${entry.category}. ${describeCategory(entry)}`}
                        />
                      </span>
                    </div>
                  );
                })}
              </div>
              {view.total === 0 ? <NoMatch onClear={clearFilters} /> : null}
              <GridPager
                idPrefix="timeline"
                page={page}
                pageCount={view.pageCount}
                perPage={perPage}
                onPerPage={(next) => {
                  setPerPage(next);
                  setPage(1);
                }}
                total={view.total}
                onPrevious={() => setPage((current) => Math.max(1, current - 1))}
                onNext={onNext}
                more={loaded?.cursor !== undefined}
                fetching={fetching}
              />
            </StateView>

            {note === undefined ? null : (
              <p className="records__bounded" role="status">
                {note}
              </p>
            )}
          </div>
        )}

        {/* The design's closing note, word for word, in its own `info` kind. */}
        <div className="records__note records__note--info">
          <span aria-hidden="true" className="records__note-glyph">
            info
          </span>
          <span>
            Rows from systems are read-only. Agents add notes and replies; they never change the
            payment state.
          </span>
        </div>
      </section>

      {/* The selected row, in full. */}
      {selectedEntry === undefined ? null : (
        <RecordDetail
          record={timelineDetail(selectedEntry)}
          open
          onClose={() => setSelectedEntryId(undefined)}
        />
      )}
    </div>
  );
}

/**
 * What a category means, for the badge's title.
 *
 * `category` is an open string on the contract, so this covers the values the design's own rows
 * carry and says so for anything else. The one that matters is `outcome_unknown`, which is what
 * `07 Payment Outcome Unknown.dc.html` is a whole design file about: an agent must not tell a
 * customer they were not charged.
 */
function describeCategory(entry: TimelineEntry): string {
  switch (entry.category) {
    case "sent":
      return "A system sent something and it left.";
    case "shown":
      return "The customer's app showed something.";
    case "opened":
      return "A record was created or opened.";
    case "waiting":
      return "A system is waiting on an answer from somewhere else.";
    case "outcome_unknown":
    case "outcome unknown":
      return "A call did not answer, so neither success nor failure is true yet. Do not tell the customer either one.";
    case "customer":
    case "customer_message":
      return "The customer said something. This is the half of the timeline a person wrote.";
    default:
      return "This console has no entry for that category, so the badge is drawn unclassified rather than given a colour nobody decided.";
  }
}

/**
 * The rectangle an export writes: the columns on screen, in the words on screen.
 *
 * Two columns more than the board. `When` on screen is a time of day, and a row read out of a file
 * months later needs the date — so the export writes the published instant in full. `Source event`
 * is the identifier somebody asking another team will be asked for, which `entryId` is not.
 */
function sheetFor(rows: readonly TimelineEntry[]): {
  header: readonly string[];
  rows: readonly (readonly string[])[];
} {
  return {
    header: ["When", "Source", "Event", "State", "Happened", "Source event"],
    rows: rows.map((entry) => [
      whenCell(entry.occurredAt),
      entry.source,
      eventCell(entry),
      stateOf(entry).label,
      entry.occurredAt,
      entry.sourceEventId,
    ]),
  };
}
